import { ref, computed } from "vue";
import { watchDebounced } from "@vueuse/core";
import type { SelectOption } from "@/components/episto-ui/select";

export interface PaginatedOptionsPage {
  options: SelectOption[];
  count: number;
}

export const usePaginatedOptions = (
  fetchPage: (page: number, search: string) => Promise<PaginatedOptionsPage>,
  debounce = 300,
) => {
  const options = ref<SelectOption[]>([]);
  const count = ref(0);
  const page = ref(0);
  const searchQuery = ref("");
  const loading = ref(false);
  let loadedQuery = "";
  let requestId = 0;

  const hasMore = computed(() => options.value.length < count.value);

  const load = async (nextPage: number, search = searchQuery.value.trim()) => {
    const currentRequest = ++requestId;
    loading.value = true;
    try {
      const result = await fetchPage(nextPage, search);
      if (currentRequest !== requestId) return;
      if (nextPage > 1) {
        const known = new Set(options.value.map((option) => String(option.value)));
        options.value.push(...result.options.filter((option) => !known.has(String(option.value))));
      } else {
        options.value = result.options;
      }
      count.value = result.count;
      page.value = nextPage;
      loadedQuery = search;
    } catch (error) {
      if (currentRequest === requestId) console.error("Failed to load options:", error);
    } finally {
      if (currentRequest === requestId) loading.value = false;
    }
  };

  const showMore = () => load(page.value + 1, loadedQuery);

  const onSearch = (query: string) => {
    searchQuery.value = query;
  };

  watchDebounced(searchQuery, () => load(1), { debounce });

  const bindings = computed(() => ({
    options: options.value,
    loading: loading.value,
    hasMore: hasMore.value,
    remote: true,
    filterable: true,
    onSearch,
    onShowMore: showMore,
  }));

  return { load, bindings };
};
